import { OverflowList } from '@theme/overflow-list';
import VariantPicker from '@theme/variant-picker';
import { ProductComponent } from '@theme/view-event-elements';
import { debounce, yieldToMainThread } from '@theme/utilities';
import { SlideshowSelectEvent } from '@theme/events';
import { addVariantToCart } from '@theme/quick-add-drawer';
import { getColorEntry } from '@theme/bt-color-catalog';
import { colourNameFromValue, colourSiblingUrl } from '@theme/bt-colour-sibling';

/**
 * Opens the cart drawer if it is closed.
 * No-ops when already open so ThemeDrawer.open() cannot replay slide-in.
 * @returns {Promise<void>}
 */
async function openCartDrawer() {
  const drawer = document.getElementById('cart-drawer');
  if (!drawer || drawer.hasAttribute('open')) return;

  if (typeof drawer.open !== 'function') {
    await customElements.whenDefined('bt-cart-drawer');
  }
  if (drawer.hasAttribute('open') || typeof drawer.open !== 'function') return;

  drawer.open();
}

/**
 * @param {HTMLElement} card
 * @param {string | undefined} sizeValue
 * @param {number} sizePosition
 * @returns {string | null}
 */
function resolveVariantIdForSize(card, sizeValue, sizePosition) {
  if (!sizeValue) return null;

  const variantsScript = card.querySelector('script[data-product-variants]');
  if (!(variantsScript instanceof HTMLScriptElement)) return null;

  /** @type {Array<Record<string, any>>} */
  let variants = [];
  try {
    variants = JSON.parse(variantsScript.textContent || '[]');
  } catch {
    return null;
  }

  const match = variants.find((variant) => {
    if (!variant.available) return false;
    return variant[`option${sizePosition}`] === sizeValue;
  });

  return match?.id != null ? String(match.id) : null;
}

/**
 * View-transition wrapper for product cards, featured product, and resource cards.
 * @typedef {object} ProductCardLinkRefs
 * @property {HTMLElement} [cardGallery]
 * @property {HTMLImageElement[]} [imagesToTransition]
 * @template {ProductCardLinkRefs} [T=ProductCardLinkRefs]
 * @extends {ProductComponent<T>}
 */
export class ProductCardLink extends ProductComponent {
  get productTransitionEnabled() {
    return this.getAttribute('data-product-transition') === 'true';
  }

  get featuredMediaUrl() {
    return this.getAttribute('data-featured-media-url');
  }

  /**
   * @param {Event} event
   */
  handleViewTransition(event) {
    if (event.defaultPrevented) return;

    if (event.target instanceof Element) {
      const interactiveElement = event.target.closest(
        'button, input, label, select, [tabindex="1"], .product-card__color-dot-button'
      );
      if (interactiveElement) return;
    }

    if (!this.productTransitionEnabled) return;

    const { cardGallery } = this.refs;
    if (!cardGallery || !cardGallery.hasAttribute('data-view-transition-to-main-product')) return;

    const { imagesToTransition } = this.refs;
    const activeImage =
      imagesToTransition?.find(
        (/** @type {HTMLImageElement} */ image) =>
          image.closest('slideshow-slide')?.getAttribute('aria-hidden') === 'false'
      ) || imagesToTransition?.[imagesToTransition.length - 1];

    if (activeImage instanceof HTMLImageElement) this.#setImageSrcset(activeImage);

    cardGallery.setAttribute('data-view-transition-type', 'product-image-transition');
    cardGallery.setAttribute('data-view-transition-triggered', 'true');
  }

  /**
   * @param {HTMLImageElement} image
   */
  #setImageSrcset(image) {
    if (!this.featuredMediaUrl) return;

    const currentImageUrl = new URL(image.currentSrc);
    const currentImageRawUrl = currentImageUrl.host + currentImageUrl.pathname;

    if (!this.featuredMediaUrl.includes(currentImageRawUrl)) {
      const imageFade = image.animate([{ opacity: 0.8 }, { opacity: 1 }], {
        duration: 125,
        easing: 'ease-in-out',
      });
      imageFade.onfinish = () => {
        image.srcset = this.featuredMediaUrl ?? '';
      };
    }
  }
}

if (!customElements.get('product-card-link')) {
  customElements.define('product-card-link', ProductCardLink);
}

/**
 * @typedef {object} ProductCardRefs
 * @property {HTMLAnchorElement} productCardLink
 * @property {HTMLAnchorElement} [productTitleLink]
 * @property {HTMLAnchorElement} [cardGalleryLink]
 * @property {import('slideshow').Slideshow} [slideshow]
 * @property {HTMLElement} [cardGallery]
 * @property {HTMLElement} [bagButton]
 * @property {HTMLImageElement[]} [imagesToTransition]
 * @extends {ProductCardLink<ProductCardRefs>}
 */
export class ProductCard extends ProductCardLink {
  requiredRefs = ['productCardLink'];

  /** @type {number | null} */
  #previousSlideIndex = null;

  /** @type {boolean} */
  #sizeAddInFlight = false;

  get productPageUrl() {
    const link = this.refs.productCardLink;
    if (!link.getAttribute('href')) return '';

    const url = new URL(link.href);
    const variantOverride = this.#getProductPageVariantOverride();
    if (variantOverride?.action === 'set') {
      url.searchParams.set('variant', variantOverride.variantId);
    } else if (variantOverride?.action === 'remove') {
      url.searchParams.delete('variant');
    }
    return url.toString();
  }

  /**
   * Variant ID selected on the card that is not yet in the product URL.
   * Used by Horizon quick-add / hotspots.
   * @returns {string | null}
   */
  getSelectedVariantId() {
    const variantOverride = this.#getProductPageVariantOverride();
    return variantOverride?.action === 'set' ? variantOverride.variantId : null;
  }

  /**
   * @returns {{ action: 'set', variantId: string } | { action: 'remove' } | null}
   */
  #getProductPageVariantOverride() {
    const link = this.refs.productCardLink;
    if (link.getAttribute('href')) {
      const url = new URL(link.href);
      if (url.searchParams.has('variant')) return null;
    }

    const variantId = this.#checkedVariantId;
    if (variantId === undefined) return null;
    return variantId ? { action: 'set', variantId } : { action: 'remove' };
  }

  /** @returns {string | undefined} */
  get #checkedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );
    return checkedInput?.dataset.variantId;
  }

  connectedCallback() {
    super.connectedCallback();

    if (!(this.refs.productCardLink instanceof HTMLAnchorElement)) {
      throw new Error('Product card link not found');
    }

    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    this.addEventListener('click', this.navigateToProduct);
    this.#bindMediaSwipeGuard();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
  }

  /**
   * Ignore the click that some browsers fire at the end of a horizontal swipe
   * so the card does not navigate while the customer is changing images.
   * Only a real track scroll counts — pointer travel alone must not block
   * desktop clicks onto the product image.
   */
  #bindMediaSwipeGuard() {
    const tracks = this.querySelectorAll('.bt-nosto-card__media-frame--swipe, .card-gallery slideshow-slides');
    for (const track of tracks) {
      if (!(track instanceof HTMLElement) || track.dataset.swipeGuard === 'true') continue;
      track.dataset.swipeGuard = 'true';

      let startScroll = 0;
      let ignoreClick = false;

      track.addEventListener(
        'pointerdown',
        () => {
          startScroll = track.scrollLeft;
          ignoreClick = false;
        },
        { passive: true }
      );
      track.addEventListener(
        'pointerup',
        () => {
          ignoreClick = Math.abs(track.scrollLeft - startScroll) > 10;
        },
        { passive: true }
      );
      track.addEventListener(
        'click',
        (event) => {
          if (!ignoreClick) return;
          event.preventDefault();
          event.stopPropagation();
          ignoreClick = false;
        },
        true
      );
    }
  }

  /**
   * @param {SlideshowSelectEvent} event
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) this.#previousSlideIndex = event.detail.index;
  };

  /**
   * @param {string} id
   */
  previewVariant(id) {
    const { slideshow } = this.refs;
    if (!slideshow) return;
    this.resetVariant.cancel();
    slideshow.select({ id }, undefined, { animate: false });
  }

  /**
   * @param {PointerEvent} event
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;
    const { slideshow } = this.refs;
    if (!slideshow) return;

    this.resetVariant.cancel();
    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      slideshow.select(this.#previousSlideIndex, undefined, { animate: false });
    } else {
      slideshow.next(undefined, { animate: false });
    }
  }

  /**
   * @param {PointerEvent} event
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;
    this.#resetVariant();
  }

  #resetVariant = () => {
    const { slideshow } = this.refs;
    if (!slideshow) return;

    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    slideshow.previous(undefined, { animate: false });
  };

  resetVariant = debounce(this.#resetVariant, 100);

  /**
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element) || this.hasAttribute('data-no-navigation')) return;
    if (event.target.closest('button, input, label, select, [tabindex="1"]')) return;

    const link = this.refs.productCardLink;
    if (!link.href) return;

    const productCardAnchor = link.getAttribute('id');
    const infiniteResultsList = this.closest('results-list[infinite-scroll="true"]');
    if (productCardAnchor && !window.Shopify.designMode && infiniteResultsList) {
      const url = new URL(window.location.href);
      const parent = this.closest('li');
      url.hash = productCardAnchor;
      if (parent?.dataset.page) url.searchParams.set('page', parent.dataset.page);
      yieldToMainThread().then(() => history.replaceState({}, '', url.toString()));
    }

    if (event.target.closest('a')) return;

    const linkURL = new URL(link.href);
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);
    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(linkURL.href, '_blank');
      return;
    }
    window.location.href = linkURL.href;
  };

  /**
   * @param {Event} event
   */
  handleWishlistToggle(event) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation?.();

    const button =
      event.target instanceof Element ? event.target.closest('.product-card__wishlist') : null;
    if (!(button instanceof HTMLElement) || button.dataset.busy === 'true') return;

    const link = this.refs.productCardLink;
    document.dispatchEvent(
      new CustomEvent('wishlist:toggle', {
        bubbles: true,
        detail: {
          productId: button.dataset.productId || this.getAttribute('data-product-id') || '',
          variantId: button.dataset.variantId || '',
          url: link instanceof HTMLAnchorElement ? link.href : '',
          button,
        },
      })
    );
  }

  /**
   * @param {Event} event
   */
  async openQuickAddDrawer(event) {
    event.preventDefault();
    event.stopPropagation();

    if (this.dataset.usesSellingPlans === 'true') {
      const url = this.productPageUrl;
      if (url) window.location.href = url;
      return;
    }

    const drawer = document.getElementById('quick-add-drawer');
    if (!drawer) return;
    if (typeof drawer.openForCard !== 'function') await customElements.whenDefined('quick-add-drawer');
    if (typeof drawer.openForCard !== 'function') return;
    await drawer.openForCard(this);
  }

  /**
   * @param {Event} event
   */
  async handleSizeQuickAdd(event) {
    event.preventDefault();
    event.stopPropagation();

    const button = event.target instanceof Element ? event.target.closest('button') : null;
    if (!(button instanceof HTMLButtonElement) || button.disabled || button.dataset.available === 'false') return;

    const variantId =
      resolveVariantIdForSize(this, button.dataset.sizeValue, Number(button.dataset.optionPosition || 0)) ||
      button.dataset.variantId;
    if (!variantId) return;

    await this.#addVariantAndOpenCart(variantId, button.dataset.sizeValue);
  }

  /**
   * @param {string} variantId
   * @param {string} [size]
   */
  async #addVariantAndOpenCart(variantId, size = '') {
    if (this.#sizeAddInFlight || !variantId) return;

    this.#sizeAddInFlight = true;
    try {
      await openCartDrawer();
      await addVariantToCart(this, variantId, 'product-card', this.#previewLine(variantId, size));
    } finally {
      this.#sizeAddInFlight = false;
    }
  }

  /**
   * @param {string} variantId
   * @param {string} size
   */
  #previewLine(variantId, size) {
    const image = this.querySelector('.product-media__image, .card-gallery img, .product-card__media img');

    return {
      variantId: String(variantId),
      title: this.querySelector('.product-card__title')?.textContent?.trim() ?? '',
      size: size.trim().toLowerCase(),
      price: this.querySelector('.product-card__price-row .price')?.textContent?.trim() ?? '',
      imageSrc: image instanceof HTMLImageElement ? image.currentSrc || image.src : '',
    };
  }
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * Used by featured-product / hotspot swatches (`variant-swatches`).
 * @extends {VariantPicker}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  /**
   * @param {Event} event
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const hasAvailableVariant = event.target.dataset.hasAvailableVariant === 'true';
    const firstAvailableVariantId = event.target.dataset.firstAvailableOrFirstVariantId;

    if (isSwatchInput && hasAvailableVariant && firstAvailableVariantId) {
      event.stopPropagation();
      this.updateSelectedOption(event.target);
      this.fetchUpdatedSection(this.buildRequestUrl(event.target, 'product-card', [event.target.dataset.optionValueId || '']), {
        detail: {
          optionValueId: event.target.dataset.optionValueId || '',
          variantId: firstAvailableVariantId,
          connectedProductUrl: event.target.dataset.connectedProductUrl || '',
        },
      });
      return;
    }

    super.variantChanged(event);
  }

  /**
   * @param {Event} [event]
   */
  showAllSwatches(event) {
    event?.preventDefault();
    const { overflowList } = this.refs;
    if (overflowList instanceof OverflowList) overflowList.showAll();
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}

/**
 * Colour dots from `@theme/bt-color-catalog` (`#bt-color-catalog`). Each swatch links to that colourway
 * via `@theme/bt-colour-sibling` (same handle rule as the PDP).
 */
class ProductCardSwatches extends HTMLElement {
  connectedCallback() {
    this.#paintCatalogSwatches();
  }

  #paintCatalogSwatches() {
    if (this.dataset.swatchSource !== 'catalog') return;

    const listEl = this.querySelector('[data-swatch-list]');
    if (!(listEl instanceof HTMLElement)) return;

    const currentColor = colourNameFromValue(this.dataset.currentColor).toLowerCase();

    const unique = [];
    const seen = new Set();
    for (const key of (this.dataset.availableColors || '').split(',')) {
      const entry = getColorEntry(key);
      if (!entry) continue;
      const titleKey = colourNameFromValue(entry.colour_title).toLowerCase();
      if (!titleKey || seen.has(titleKey)) continue;
      seen.add(titleKey);
      unique.push(entry);
    }

    listEl.replaceChildren();
    const card = this.closest('product-card');
    const cardLink = card?.querySelector('[ref="productCardLink"]');
    const productUrl = this.dataset.productUrl || cardLink?.getAttribute('href') || '';
    const productHandle = this.dataset.productHandle || card?.dataset.productHandle || '';

    const limitDesktop = Number(this.dataset.swatchLimitDesktop) || 0;
    const limitMobile = Number(this.dataset.swatchLimitMobile) || 0;
    const overflowLabel = this.dataset.overflowLabel || '+__COUNT__ more';
    const renderLimit = limitDesktop > 0 || limitMobile > 0 ? Math.max(limitDesktop, limitMobile) : 0;
    const visible = renderLimit > 0 ? unique.slice(0, renderLimit) : unique;

    visible.forEach((entry, index) => {
      const title = colourNameFromValue(entry.colour_title);
      const isCurrent = title.toLowerCase() === currentColor;
      const item = document.createElement('li');
      const classes = ['product-card__swatch-item'];
      if (limitMobile > 0 && index >= limitMobile) classes.push('product-card__swatch-item--hide-mobile');
      if (limitDesktop > 0 && index >= limitDesktop) classes.push('product-card__swatch-item--hide-desktop');
      if (isCurrent) classes.push('product-card__swatch-item--current');
      item.className = classes.join(' ');

      const link = document.createElement('a');
      link.className = 'product-card__color-dot-button';
      link.href = colourSiblingUrl(productUrl, productHandle, currentColor, title);
      link.title = title;
      link.setAttribute('aria-label', title);
      link.setAttribute('aria-current', isCurrent ? 'true' : 'false');
      link.dataset.colourName = title;

      const dot = document.createElement('span');
      dot.className = 'product-card__color-dot';
      const hex = String(entry.colour_hex || '').trim();
      const image = String(entry.colour_img || '').trim();
      if (hex) dot.style.backgroundColor = hex;
      if (image) {
        const img = document.createElement('img');
        img.className = 'product-card__color-dot-img';
        img.src = image;
        img.alt = '';
        img.width = 24;
        img.height = 24;
        img.loading = 'eager';
        img.decoding = 'sync';
        dot.append(img);
      }
      const hidden = document.createElement('span');
      hidden.className = 'visually-hidden';
      hidden.textContent = title;
      link.append(dot, hidden);
      item.append(link);
      listEl.append(item);
    });

    const overflowMobile = limitMobile > 0 ? Math.max(0, unique.length - limitMobile) : 0;
    const overflowDesktop = limitDesktop > 0 ? Math.max(0, unique.length - limitDesktop) : 0;
    if (overflowMobile > 0 || overflowDesktop > 0) {
      const label = (count) => overflowLabel.replace('__COUNT__', String(count));
      const overflow = document.createElement('li');
      const classes = ['product-card__swatch-overflow', 'text-p4', 'color-text-subdued'];
      if (overflowMobile === 0) classes.push('product-card__swatch-overflow--hide-mobile');
      if (overflowDesktop === 0) classes.push('product-card__swatch-overflow--hide-desktop');
      overflow.className = classes.join(' ');

      if (overflowMobile > 0 && overflowDesktop > 0 && overflowMobile !== overflowDesktop) {
        const mobile = document.createElement('span');
        mobile.className = 'product-card__swatch-overflow-label--mobile';
        mobile.textContent = label(overflowMobile);
        const desktop = document.createElement('span');
        desktop.className = 'product-card__swatch-overflow-label--desktop';
        desktop.textContent = label(overflowDesktop);
        overflow.append(mobile, desktop);
      } else {
        overflow.textContent = label(overflowMobile || overflowDesktop);
      }
      listEl.append(overflow);
    }
  }
}

if (!customElements.get('product-card-swatches')) {
  customElements.define('product-card-swatches', ProductCardSwatches);
}
